import React from 'react';
import styled from 'styled-components';
import { FiUser } from 'react-icons/fi';

const MessageContainer = styled.div`
  display: flex;
  gap: 16px;
  padding: 20px;
  border-radius: 16px;
  background-color: ${props => {
    if (props.isUser) return 'rgba(0, 0, 254, 0.05)';
    if (props.isError) return '#fff2f0';
    return '#fff';
  }};
  border: 1px solid ${props => {
    if (props.isUser) return 'rgba(0, 0, 254, 0.1)';
    if (props.isError) return '#ffccc7';
    return '#eee';
  }};
  width: 100%;
  max-width: 100%;
  transition: all 0.3s ease;
  box-shadow: ${props => {
    if (props.isUser) return '0 4px 12px rgba(0, 0, 254, 0.05)';
    return '0 2px 6px rgba(0, 0, 0, 0.02)';
  }};
`;

const AvatarContainer = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${props => props.isUser ? 'linear-gradient(135deg, #0000fe, #6060ff)' : 'linear-gradient(135deg, #f0f0f0, #fff)'};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.isUser ? '#fff' : '#0000fe'};
  font-weight: bold;
  flex-shrink: 0;
  box-shadow: ${props => props.isUser ? '0 4px 10px rgba(0, 0, 254, 0.15)' : '0 2px 6px rgba(0, 0, 0, 0.05)'};
  border: ${props => props.isUser ? 'none' : '1px solid #eee'};
`;

const ContentContainer = styled.div`
  flex: 1;
  overflow: hidden;
`;

const MessageText = styled.div`
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  color:#000000;
`;

const MetadataContainer = styled.div`
  margin-top: 12px;
  font-size: 12px;
  color: #888;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  background-color: #f9f9f9;
  padding: 8px 12px;
  border-radius: 6px;
`;

const MetadataItem = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const EntityTag = styled.span`
  background-color: rgba(0, 0, 254, 0.05);
  border: 1px solid rgba(0, 0, 254, 0.15);
  border-radius: 6px;
  padding: 3px 8px;
  font-size: 12px;
  color: #0000fe;
  margin: 3px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;
  max-width: 150px;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: all 0.3s ease;
  
  &:hover {
    background-color: rgba(0, 0, 254, 0.1);
    transform: translateY(-1px);
  }
`;

const NetworkBanner = styled.div`
  margin-top: 10px;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: white;
  background: ${props => props.network === 'solana' ? 'linear-gradient(135deg, #9945FF, #8830e7)' : 'linear-gradient(135deg, #0000fe, #4b4bfe)'};
  box-shadow: 0 4px 10px ${props => props.network === 'solana' ? 'rgba(153, 69, 255, 0.15)' : 'rgba(0, 0, 254, 0.15)'};
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 12px ${props => props.network === 'solana' ? 'rgba(153, 69, 255, 0.2)' : 'rgba(0, 0, 254, 0.2)'};
  }
`;

function ChatMessage({ message }) {
  const { type, content, data, metadata, isError } = message;
  const isUser = type === 'user';
  
  return (
    <MessageContainer isUser={isUser} isError={isError}>
      <AvatarContainer isUser={isUser}>
        {isUser ? <FiUser /> : 'AI'}
      </AvatarContainer>
      <ContentContainer>
        <MessageText>{content}</MessageText>
        
        {data && data.network === 'solana' && (
          <NetworkBanner network="solana">Solana Network</NetworkBanner>
        )}
        
        
        {metadata && (
          <MetadataContainer>
            {metadata.intent && (
              <MetadataItem>
                <strong>Intent:</strong> {metadata.intent.replace(/_/g, ' ')}
              </MetadataItem>
            )}
            
            {metadata.executionTime && (
              <MetadataItem>
                <strong>⏱️ Time:</strong> {(metadata.executionTime / 1000).toFixed(2)}s
              </MetadataItem>
            )}
            
            {metadata.entities && metadata.entities.length > 0 && (
              <div style={{ width: '100%', marginTop: '6px' }}>
                <strong>Entities:</strong> 
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px', marginTop: '4px' }}>
                  {metadata.entities.map((entity, index) => {
                    // Truncate long values for display
                    const displayValue = entity.value ? 
                      (entity.value.length > 12 ? 
                        `${entity.value.substring(0, 6)}...${entity.value.substring(entity.value.length - 6)}` : 
                        entity.value) :
                      'N/A';
                      
                    // Get network badge if available
                    const networkBadge = entity.network ? 
                      (entity.network === 'solana' ? '🟣' : 
                       entity.network === 'evm' ? '🔷' : '') : '';
                    
                    return (
                      <EntityTag key={index} title={entity.value}>
                        {networkBadge} {entity.type}: {displayValue}
                      </EntityTag>
                    );
                  })}
                </div>
              </div>
            )}
          </MetadataContainer>
        )}
      </ContentContainer>
    </MessageContainer>
  );
}

export default ChatMessage;
